import './App.css';

import { Route, Outlet, createBrowserRouter, createRoutesFromElements, RouterProvider, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import NotFoundPage  from './Pages/NotFound'
import AboutPage from './Pages/About/About'
import LandingPage from './Pages/LandingPage'
import PrivacyPage from './Pages/Legal/PrivacyPage'
import TermsPage from './Pages/Legal/TermsPage'
import LoginEmail    from './Pages/LoginPages/LoginEmail';
import LoginCode     from './Pages/LoginPages/LoginCode';
import SmsVerifyPage from './Pages/LoginPages/SmsVerifyPage';
import OAuthCallback from './Pages/LoginPages/OAuthCallback';
import Profile       from './Pages/LoggedInPages/Profile/Profile';
import ConfirmChange from './Pages/LoggedInPages/Profile/ConfirmChange';
import HealthCheckProvider from './components/common/HealthCheckProvider';
import Footer from './components/common/Footer';
import CookieBanner from './components/common/CookieBanner';
import Analytics, { PageTracker } from './components/Analytics';
import { TutorialProvider } from './contexts/TutorialContext';
import { TutorialOverlay } from './components/Tutorial';

import axios from "axios";
import Cookies from "js-cookie";

import config   from './Config';
import { postAuthorized } from "./hooks/User";
import { flushSurveyOutbox } from "./utils/surveyOfflineDb";
import { ROUTES } from './routes';

import SurveysPage from './Pages/LoggedInPages/Surveys/SurveysPage';
import Patients from './Pages/LoggedInPages/Patients/Patients';
import Doctors from './Pages/LoggedInPages/Doctors/Doctors';
import SchedulerPage from './Pages/LoggedInPages/Scheduler/Scheduler';
import SurveyPassingPage from './Pages/LoggedInPages/SurveyPassing/SurveyPassingPage';
import SurveyEditorPage from './Pages/LoggedInPages/SurveyEditor/SurveyEditorPage';
import QuestionsPage from './Pages/LoggedInPages/Questions/QuestionsPage';
import QuestionEditorPage from './Pages/LoggedInPages/QuestionEditor/QuestionEditor';
import PatientScheduledSurveysPage from './Pages/LoggedInPages/PatientScheduledSurveys/PatientScheduledSurveys';
import PatientSurveysPage from './Pages/LoggedInPages/PatientSurveys/PatientSurveys';
import PatientSurveyHistoryPage from './Pages/LoggedInPages/PatientSurveyHistory/PatientSurveyHistory';
import PatientAttemptDetailPage from './Pages/LoggedInPages/PatientAttemptDetail/PatientAttemptDetail';
import PatientMedicationsPage from './Pages/LoggedInPages/PatientMedications/PatientMedications';
import MyMedicationRemindersPage from './Pages/LoggedInPages/MyMedicationReminders/MyMedicationReminders';
import MyMedicationCalendarPage from './Pages/LoggedInPages/MyMedicationCalendar/MyMedicationCalendar';
import NotificationsPage from './Pages/LoggedInPages/Notifications/NotificationsPage';
import PatientChatPage from './Pages/LoggedInPages/PatientChat/PatientChat';
import PatientGroupsPage from './Pages/LoggedInPages/PatientGroups/PatientGroups';
import PatientGroupDetailPage from './Pages/LoggedInPages/PatientGroupDetail/PatientGroupDetail';
import { DoctorNotificationsProvider } from './contexts/DoctorNotificationsContext';
import DoctorNotificationsShell from './components/doctor/DoctorNotificationsShell';

/**
 * [TODO]
 * 
 * Error handling:
 *    
 *    useErrorHandler?                                              priority: MIDDLE
 * 
 * Authorization: 
 * 
 *    useUser()                                                     priority: TOP MOST #DONE
 * 
 * Hooks:
 * 
 *    useTextInput(args)    -> JSXElement, string, stringSetter     priority: BOTTOM   
 *    - useEmailInput(args)                                         priority: BOTTOM
 *  
 *    useTrigger(() => {}, []) -> trigger, setTrigger               priority: TOP
 *    useOnlyFirstTime(() => {}, []) -> void                        priority: TOP
 * 
 * Misc.
 * 
 *    aliases for axios.get, axios.post, etc...                     priority: LOW      #DONE
 * 
 * 
 */

// Layout wrapper component that includes Footer
function PageWithFooter({ children }) {
  return (
    <div className="app-layout">
      <PageTracker />
      <main className="app-main">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Simple layout without Footer (for auth pages)
function PageWithoutFooter({ children }) {
  return (
    <div className="app-layout">
      <PageTracker />
      <main className="app-main">
        {children}
      </main>
    </div>
  );
}

// Helper to wrap component with Footer
const withFooter = (Component) => () => (
  <PageWithFooter>
    <Component />
  </PageWithFooter>
);

// Helper for pages without Footer
const withoutFooter = (Component) => () => (
  <PageWithoutFooter>
    <Component />
  </PageWithoutFooter>
);

function parseTgAuthResult(hash) {
  const marker = "tgAuthResult=";
  const i = hash.indexOf(marker);
  if (i < 0) return null;
  let b64 = decodeURIComponent(hash.slice(i + marker.length).split("&")[0]);
  b64 = b64.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return JSON.parse(atob(b64));
}

/** Корневой layout: страницы + глобальный dock уведомлений (нужен useLocation). */
function AppShell() {
  const nav = useNavigate();

  useEffect(() => {
    const payload = parseTgAuthResult(window.location.hash || "");
    if (!payload) return;
    const path = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", path);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post("/general/oauth/telegram/widget", {
          id: payload.id,
          first_name: payload.first_name,
          last_name: payload.last_name,
          username: payload.username,
          photo_url: payload.photo_url,
          auth_date: payload.auth_date,
          hash: payload.hash,
        });
        if (cancelled || !data?.user_token) return;
        Cookies.set(config.authCookie, data.user_token, { expires: 30 * 6 });
        window.dispatchEvent(new CustomEvent("user:login"));
        nav(ROUTES.PROFILE, { viewTransition: true });
      } catch {
        if (!cancelled) {
          nav(ROUTES.AUTH, { viewTransition: true, state: { error: "Не удалось войти через Telegram" } });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  return (
    <>
      <Outlet />
      <DoctorNotificationsShell />
    </>
  );
}

function App() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    const syncOutbox = async () => {
      if (!Cookies.get(config.authCookie)) return;
      const flushed = await flushSurveyOutbox(postAuthorized);
      if (flushed > 0) {
        window.dispatchEvent(
          new CustomEvent("spiria-survey-outbox-flushed", { detail: { count: flushed } })
        );
      }
    };
    window.addEventListener("online", syncOutbox);
    const onVis = () => {
      if (document.visibilityState === "visible") syncOutbox();
    };
    document.addEventListener("visibilitychange", onVis);
    syncOutbox();
    return () => {
      window.removeEventListener("online", syncOutbox);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isClient]);

  axios.defaults.headers.common["apikey"] = config.apiKey;
  axios.defaults.baseURL = config.getApiBaseUrl();

  const router = createBrowserRouter(
    createRoutesFromElements([
      <Route element={<AppShell />}>
      <Route element={withFooter(LandingPage)()} path={ROUTES.HOME}/>,
      <Route element={withoutFooter(LoginCode)()} path={ROUTES.AUTH_CODE}/>,
      <Route element={withoutFooter(SmsVerifyPage)()} path={ROUTES.AUTH_SMS_VERIFY}/>,
      <Route element={withoutFooter(LoginEmail)()} path={ROUTES.AUTH}/>,
      <Route element={withoutFooter(OAuthCallback)()} path={ROUTES.OAUTH_CALLBACK}/>,
      <Route element={withFooter(Profile)()} path={ROUTES.PROFILE} />,
      <Route element={withFooter(SurveysPage)()} path={ROUTES.SURVEYS}/>,
      <Route element={withFooter(SurveyEditorPage)()} path={ROUTES.SURVEY_EDITOR}/>,
      <Route element={withFooter(QuestionsPage)()} path={ROUTES.QUESTIONS}/>,
      <Route element={withFooter(QuestionEditorPage)()} path={ROUTES.QUESTION_EDITOR}/>,
      <Route element={withFooter(PatientScheduledSurveysPage)()} path={ROUTES.PATIENT_SCHEDULED}/>,
      <Route element={withFooter(PatientMedicationsPage)()} path={ROUTES.PATIENT_MEDICATIONS}/>,
      <Route element={withFooter(PatientSurveysPage)()} path={ROUTES.MY_SURVEYS}/>,
      <Route element={withFooter(PatientSurveyHistoryPage)()} path={ROUTES.SURVEY_HISTORY}/>,
      <Route element={withFooter(PatientAttemptDetailPage)()} path={ROUTES.SURVEY_ATTEMPT_DETAIL}/>,
      <Route element={withFooter(MyMedicationRemindersPage)()} path={ROUTES.MY_MEDICATIONS}/>,
      <Route element={withFooter(MyMedicationCalendarPage)()} path={ROUTES.MY_MEDICATION_CALENDAR}/>,
      <Route element={withFooter(SurveyPassingPage)()} path={ROUTES.TAKE_SURVEY}/>,
      <Route element={withFooter(SchedulerPage)()} path={ROUTES.SCHEDULER}/>,
      <Route element={withFooter(Patients)()} path={ROUTES.PATIENTS}/>,
      <Route element={withFooter(PatientGroupsPage)()} path={ROUTES.PATIENT_GROUPS}/>,
      <Route element={withFooter(PatientGroupDetailPage)()} path={ROUTES.PATIENT_GROUP_DETAIL}/>,
      <Route element={withFooter(PatientChatPage)()} path={ROUTES.PATIENT_CHAT}/>,
      <Route element={withFooter(NotificationsPage)()} path={ROUTES.NOTIFICATIONS}/>,
      <Route element={withFooter(Doctors)()} path={ROUTES.DOCTORS}/>,
      <Route element={withFooter(ConfirmChange)()} path={ROUTES.CONFIRM_CHANGE}/>,
      <Route element={withFooter(AboutPage)()} path={ROUTES.ABOUT}/>,
      <Route element={withFooter(PrivacyPage)()} path="/privacy"/>,
      <Route element={withFooter(TermsPage)()} path="/terms"/>,
      <Route element={withFooter(NotFoundPage)()} path={ROUTES.NOT_FOUND}/>
      </Route>
    ])
  );

  return (
    <HealthCheckProvider>
      {isClient ? (
        <TutorialProvider>
          <DoctorNotificationsProvider>
            <Analytics />
            <CookieBanner />
            <TutorialOverlay />
            <RouterProvider router={router} />
          </DoctorNotificationsProvider>
        </TutorialProvider>
      ) : (
        <DoctorNotificationsProvider>
          <Analytics />
          <CookieBanner />
          <RouterProvider router={router} />
        </DoctorNotificationsProvider>
      )}
    </HealthCheckProvider>
  );
}

export default App;
